import { CommonValidator } from '../utils/commonValidator';
import { EncoderToolType, EncodingOperation, HtmlEntityConfiguration, HtmlEntityMode } from '../types';

export class HtmlEntityService {
  private static readonly HTML_ENTITIES = {
    // Basic HTML entities
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '`': '&#96;',

    // Extended HTML entities
    ' ': '&nbsp;',
    '¢': '&cent;',
    '£': '&pound;',
    '¥': '&yen;',
    '€': '&euro;',
    '©': '&copy;',
    '®': '&reg;',
    '™': '&trade;',

    // Mathematical symbols
    '±': '&plusmn;',
    '×': '&times;',
    '÷': '&divide;',
    '≠': '&ne;',
    '≤': '&le;',
    '≥': '&ge;',
    '∞': '&infin;',
    '∑': '&sum;',
    '∏': '&prod;',
    '√': '&radic;',
    '∫': '&int;',
    '∂': '&part;',
    '∇': '&nabla;',
    '∆': '&Delta;',
    '∅': '&empty;',

    // Greek letters
    'α': '&alpha;',
    'β': '&beta;',
    'γ': '&gamma;',
    'δ': '&delta;',
    'ε': '&epsilon;',
    'ζ': '&zeta;',
    'η': '&eta;',
    'θ': '&theta;',
    'ι': '&iota;',
    'κ': '&kappa;',
    'λ': '&lambda;',
    'μ': '&mu;',
    'ν': '&nu;',
    'ξ': '&xi;',
    'ο': '&omicron;',
    'π': '&pi;',
    'ρ': '&rho;',
    'σ': '&sigma;',
    'τ': '&tau;',
    'υ': '&upsilon;',
    'φ': '&phi;',
    'χ': '&chi;',
    'ψ': '&psi;',
    'ω': '&omega;',

    'Α': '&Alpha;',
    'Β': '&Beta;',
    'Γ': '&Gamma;',
    'Δ': '&Delta;',
    'Ε': '&Epsilon;',
    'Ζ': '&Zeta;',
    'Η': '&Eta;',
    'Θ': '&Theta;',
    'Ι': '&Iota;',
    'Κ': '&Kappa;',
    'Λ': '&Lambda;',
    'Μ': '&Mu;',
    'Ν': '&Nu;',
    'Ξ': '&Xi;',
    'Ο': '&Omicron;',
    'Π': '&Pi;',
    'Ρ': '&Rho;',
    'Σ': '&Sigma;',
    'Τ': '&Tau;',
    'Υ': '&Upsilon;',
    'Φ': '&Phi;',
    'Χ': '&Chi;',
    'Ψ': '&Psi;',
    'Ω': '&Omega;',

    // Punctuation and special characters
    '…': '&hellip;',
    '–': '&ndash;',
    '—': '&mdash;',
    '\u2018': '&lsquo;',
    '\u2019': '&rsquo;',
    '\u201c': '&ldquo;',
    '\u201d': '&rdquo;',
    '«': '&laquo;',
    '»': '&raquo;',
    '‹': '&lsaquo;',
    '›': '&rsaquo;',

    // Accented characters
    'à': '&agrave;',
    'á': '&aacute;',
    'â': '&acirc;',
    'ã': '&atilde;',
    'ä': '&auml;',
    'å': '&aring;',
    'æ': '&aelig;',
    'ç': '&ccedil;',
    'è': '&egrave;',
    'é': '&eacute;',
    'ê': '&ecirc;',
    'ë': '&euml;',
    'ì': '&igrave;',
    'í': '&iacute;',
    'î': '&icirc;',
    'ï': '&iuml;',
    'ñ': '&ntilde;',
    'ò': '&ograve;',
    'ó': '&oacute;',
    'ô': '&ocirc;',
    'õ': '&otilde;',
    'ö': '&ouml;',
    'ø': '&oslash;',
    'ù': '&ugrave;',
    'ú': '&uacute;',
    'û': '&ucirc;',
    'ü': '&uuml;',
    'ÿ': '&yuml;',

    'À': '&Agrave;',
    'Á': '&Aacute;',
    'Â': '&Acirc;',
    'Ã': '&Atilde;',
    'Ä': '&Auml;',
    'Å': '&Aring;',
    'Æ': '&AElig;',
    'Ç': '&Ccedil;',
    'È': '&Egrave;',
    'É': '&Eacute;',
    'Ê': '&Ecirc;',
    'Ë': '&Euml;',
    'Ì': '&Igrave;',
    'Í': '&Iacute;',
    'Î': '&Icirc;',
    'Ï': '&Iuml;',
    'Ñ': '&Ntilde;',
    'Ò': '&Ograve;',
    'Ó': '&Oacute;',
    'Ô': '&Ocirc;',
    'Õ': '&Otilde;',
    'Ö': '&Ouml;',
    'Ø': '&Oslash;',
    'Ù': '&Ugrave;',
    'Ú': '&Uacute;',
    'Û': '&Ucirc;',
    'Ü': '&Uuml;',
    'Ÿ': '&Yuml;'
  };

  private static readonly REVERSE_HTML_ENTITIES: Record<string, string> = {};

  static {
    // Build reverse lookup for decoding
    for (const [char, entity] of Object.entries(this.HTML_ENTITIES)) {
      this.REVERSE_HTML_ENTITIES[entity] = char;
    }
  }

  /**
   * Creates an HTML entity configuration
   */
  static createConfiguration(
    input: string,
    mode: HtmlEntityMode = HtmlEntityMode.ENCODE,
    options: {
      encodeSpaces?: boolean;
      encodeAllChars?: boolean;
      preserveLineBreaks?: boolean;
      customEntities?: Record<string, string>;
    } = {}
  ): HtmlEntityConfiguration {
    return {
      input,
      mode,
      encodeSpaces: options.encodeSpaces ?? false,
      encodeAllChars: options.encodeAllChars ?? false,
      preserveLineBreaks: options.preserveLineBreaks ?? true,
      customEntities: options.customEntities ?? {}
    };
  }

  /**
   * Validates HTML entity configuration
   */
  static validateConfiguration(config: HtmlEntityConfiguration): void {
    if (!config.input) {
      throw new Error('Input text is required');
    }

    if (!Object.values(HtmlEntityMode).includes(config.mode)) {
      throw new Error('Invalid mode specified');
    }

    if (typeof config.encodeSpaces !== 'boolean') {
      throw new Error('encodeSpaces must be a boolean');
    }

    if (typeof config.encodeAllChars !== 'boolean') {
      throw new Error('encodeAllChars must be a boolean');
    }

    if (typeof config.preserveLineBreaks !== 'boolean') {
      throw new Error('preserveLineBreaks must be a boolean');
    }

    if (config.customEntities && typeof config.customEntities !== 'object') {
      throw new Error('customEntities must be an object');
    }
  }

  /**
   * Encodes HTML entities in text
   */
  static async encode(config: HtmlEntityConfiguration): Promise<EncodingOperation> {
    try {
      this.validateConfiguration(config);

      const validation = CommonValidator.validateText(
        config.input,
        CommonValidator.getValidationRules(EncoderToolType.HTML_ENTITY)
      );

      if (!validation.isValid) {
        return {
          id: `htmlentity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          status: 'error',
          errorMessage: validation.errors?.join(', ') || 'Invalid input text',
          operationType: 'encode',
          toolType: EncoderToolType.HTML_ENTITY,
          timestamp: new Date(),
          inputData: config.input,
          outputData: null
        };
      }

      let result = config.input;

      if (config.encodeAllChars) {
        result = this.encodeAllCharacters(result, config);
      } else {
        result = this.encodeHtmlEntities(result, config);
      }

      return {
        id: `htmlentity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'success',
        operationType: 'encode',
        toolType: EncoderToolType.HTML_ENTITY,
        timestamp: new Date(),
        inputData: config.input,
        outputData: result,
        metadata: {
          originalLength: config.input.length,
          encodedLength: result.length,
          compressionRatio: config.input.length > 0 ? result.length / config.input.length : 0,
          mode: config.mode,
          encodeSpaces: config.encodeSpaces,
          encodeAllChars: config.encodeAllChars,
          preserveLineBreaks: config.preserveLineBreaks
        }
      };

    } catch (error) {
      return {
        id: `htmlentity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred',
        operationType: 'encode',
        toolType: EncoderToolType.HTML_ENTITY,
        timestamp: new Date(),
        inputData: config.input,
        outputData: null
      };
    }
  }

  /**
   * Decodes HTML entities in text
   */
  static async decode(config: HtmlEntityConfiguration): Promise<EncodingOperation> {
    try {
      this.validateConfiguration(config);

      const validation = CommonValidator.validateText(
        config.input,
        CommonValidator.getValidationRules(EncoderToolType.HTML_ENTITY)
      );

      if (!validation.isValid) {
        return {
          id: `htmlentity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          status: 'error',
          errorMessage: validation.errors?.join(', ') || 'Invalid input text',
          operationType: 'decode',
          toolType: EncoderToolType.HTML_ENTITY,
          timestamp: new Date(),
          inputData: config.input
        };
      }

      const result = this.decodeHtmlEntities(config.input, config);

      return {
        id: `htmlentity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'success',
        operationType: 'decode',
        toolType: EncoderToolType.HTML_ENTITY,
        timestamp: new Date(),
        inputData: config.input,
        outputData: result,
        metadata: {
          originalLength: config.input.length,
          decodedLength: result.length,
          compressionRatio: config.input.length > 0 ? result.length / config.input.length : 0,
          mode: config.mode,
          preserveLineBreaks: config.preserveLineBreaks
        }
      };

    } catch (error) {
      return {
        id: `htmlentity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred',
        operationType: 'decode',
        toolType: EncoderToolType.HTML_ENTITY,
        timestamp: new Date(),
        inputData: config.input,
        outputData: null
      };
    }
  }

  /**
   * Main processing function
   */
  static async process(config: HtmlEntityConfiguration): Promise<EncodingOperation> {
    if (config.mode === HtmlEntityMode.ENCODE) {
      return this.encode(config);
    } else if (config.mode === HtmlEntityMode.DECODE) {
      return this.decode(config);
    } else {
      throw new Error('Unsupported mode');
    }
  }

  /**
   * Encodes HTML entities in text
   */
  private static encodeHtmlEntities(text: string, config: HtmlEntityConfiguration): string {
    let result = text;

    // Preserve line breaks if requested
    if (config.preserveLineBreaks) {
      result = result.replace(/\n/g, '\n');
    }

    // Encode basic HTML entities FIRST (before spaces to avoid double-encoding &nbsp;)
    result = result.replace(/&/g, '&amp;');
    result = result.replace(/</g, '&lt;');
    result = result.replace(/>/g, '&gt;');
    result = result.replace(/"/g, '&quot;');
    result = result.replace(/'/g, '&#39;');

    // Encode spaces if requested (AFTER basic entities)
    if (config.encodeSpaces) {
      result = result.replace(/ /g, '&nbsp;');
    }

    // Encode additional entities from our map
    for (const [char, entity] of Object.entries(this.HTML_ENTITIES)) {
      // Skip already encoded basic entities and space (handled separately above)
      if (['&', '<', '>', '"', "'", ' '].includes(char)) continue;

      const regex = new RegExp(this.escapeRegExp(char), 'g');
      result = result.replace(regex, entity);
    }

    // Apply custom entities
    if (config.customEntities) {
      for (const [char, entity] of Object.entries(config.customEntities)) {
        const regex = new RegExp(this.escapeRegExp(char), 'g');
        result = result.replace(regex, entity);
      }
    }

    return result;
  }

  /**
   * Encodes all characters to HTML entities
   */
  private static encodeAllCharacters(text: string, config: HtmlEntityConfiguration): string {
    let result = '';

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (config.preserveLineBreaks && char === '\n') {
        result += '\n';
        continue;
      }

      if (config.encodeSpaces && char === ' ') {
        result += '&nbsp;';
        continue;
      }

      // Check if we have a named entity
      if (this.HTML_ENTITIES[char as keyof typeof this.HTML_ENTITIES]) {
        result += this.HTML_ENTITIES[char as keyof typeof this.HTML_ENTITIES];
      } else {
        // Use numeric entity for other characters
        const code = char.charCodeAt(0);
        if (code < 128) {
          result += `&#${code};`;
        } else {
          result += `&#x${code.toString(16).toUpperCase()};`;
        }
      }
    }

    return result;
  }

  /**
   * Decodes HTML entities in text
   */
  private static decodeHtmlEntities(text: string, config: HtmlEntityConfiguration): string {
    let result = text;

    // Decode named entities
    for (const [entity, char] of Object.entries(this.REVERSE_HTML_ENTITIES)) {
      const regex = new RegExp(this.escapeRegExp(entity), 'g');
      result = result.replace(regex, char);
    }

    // Decode numeric entities (decimal)
    result = result.replace(/&#(\d+);/g, (_match, code) => {
      const charCode = parseInt(code, 10);
      return String.fromCharCode(charCode);
    });

    // Decode numeric entities (hexadecimal)
    result = result.replace(/&#x([0-9A-Fa-f]+);/g, (_match, code) => {
      const charCode = parseInt(code, 16);
      return String.fromCharCode(charCode);
    });

    // Apply custom entity decoding
    if (config.customEntities) {
      for (const [char, entity] of Object.entries(config.customEntities)) {
        const regex = new RegExp(this.escapeRegExp(entity), 'g');
        result = result.replace(regex, char);
      }
    }

    return result;
  }

  /**
   * Escapes special regex characters
   */
  private static escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Gets list of supported HTML entities
   */
  static getSupportedEntities(): { character: string; entity: string; name?: string }[] {
    return Object.entries(this.HTML_ENTITIES).map(([char, entity]) => ({
      character: char,
      entity,
      name: entity.replace(/[&;]/g, '')
    }));
  }

  /**
   * Checks if a character has an HTML entity
   */
  static hasEntity(character: string): boolean {
    return character in this.HTML_ENTITIES;
  }

  /**
   * Gets the HTML entity for a character
   */
  static getEntity(character: string): string | null {
    return this.HTML_ENTITIES[character as keyof typeof this.HTML_ENTITIES] || null;
  }

  /**
   * Validates if text contains HTML entities
   */
  static containsEntities(text: string): boolean {
    return /&[a-zA-Z]+;|&#\d+;|&#x[0-9A-Fa-f]+;/.test(text);
  }

  /**
   * Counts entities in text
   */
  static countEntities(text: string): number {
    const matches = text.match(/&[a-zA-Z]+;|&#\d+;|&#x[0-9A-Fa-f]+;/g);
    return matches ? matches.length : 0;
  }

  /**
   * Batch process multiple texts
   */
  static async batchProcess(configs: HtmlEntityConfiguration[]): Promise<EncodingOperation[]> {
    return Promise.all(configs.map(config => this.process(config)));
  }

  /**
   * Converts between entity formats
   */
  static convertEntityFormat(
    text: string,
    _fromFormat: 'named' | 'decimal' | 'hexadecimal',
    toFormat: 'named' | 'decimal' | 'hexadecimal'
  ): string {
    let result = text;

    // Convert to characters first
    result = this.decodeHtmlEntities(result, {
      input: result,
      mode: HtmlEntityMode.DECODE,
      encodeSpaces: false,
      encodeAllChars: false,
      preserveLineBreaks: true,
      customEntities: {}
    });

    // Then re-encode in desired format
    if (toFormat === 'decimal') {
      result = result.replace(/./g, (char) => {
        if (char === '\n') return '\n';
        const code = char.charCodeAt(0);
        return `&#${code};`;
      });
    } else if (toFormat === 'hexadecimal') {
      result = result.replace(/./g, (char) => {
        if (char === '\n') return '\n';
        const code = char.charCodeAt(0);
        return `&#x${code.toString(16).toUpperCase()};`;
      });
    } else {
      result = this.encodeHtmlEntities(result, {
        input: '',
        mode: HtmlEntityMode.ENCODE,
        encodeSpaces: false,
        encodeAllChars: false,
        preserveLineBreaks: true,
        customEntities: {}
      });
    }

    return result;
  }

  /**
   * Gets statistics about HTML entity usage
   */
  static getEntityStats(text: string): {
    totalEntities: number;
    namedEntities: number;
    numericEntities: number;
    hexadecimalEntities: number;
    uniqueEntities: string[];
    mostCommonEntity: { entity: string; count: number } | null;
  } {
    const matches = text.match(/&[a-zA-Z]+;|&#\d+;|&#x[0-9A-Fa-f]+;/g) || [];

    const namedEntities = matches.filter(m => /^&[a-zA-Z]+;$/.test(m)).length;
    const numericEntities = matches.filter(m => /^&#\d+;$/.test(m)).length;
    const hexadecimalEntities = matches.filter(m => /^&#x[0-9A-Fa-f]+;$/.test(m)).length;

    const uniqueEntities = [...new Set(matches)];

    const entityCounts = matches.reduce((acc, entity) => {
      acc[entity] = (acc[entity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostCommonEntity = Object.entries(entityCounts).reduce((max, [entity, count]) => {
      return count > (max?.count || 0) ? { entity, count } : max;
    }, null as { entity: string; count: number } | null);

    return {
      totalEntities: matches.length,
      namedEntities,
      numericEntities,
      hexadecimalEntities,
      uniqueEntities,
      mostCommonEntity
    };
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}